import { useEffect, useMemo, useState } from "react";
import { Activity, ExternalLink, Loader2 } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";

const STROKE = 1.5;

/**
 * Internal provider key → customer-visible bucket label.
 * Backend keys (openai/sarvam/google) stay untouched — this map is the
 * presentation-layer scrub so vendor and model names never appear in the
 * Usage UI. Admin still gets full cost visibility via the bucket totals,
 * which is all that's needed for unit-economics work.
 *
 *   openai (Whisper)          → "Speech recognition · backup engine"
 *   sarvam                    → "Speech recognition · India & Others"
 *   assemblyai                → "Speech recognition · US & Canada"
 *   google (Gemini)           → "Summarisation & AI chat"
 *   anything else             → "Other"
 *
 * Color tokens are tied to the bucket, not the vendor — same purpose
 * across renders, no leaking of internal identifiers via class names.
 */
const BUCKET_LABELS = {
  openai: "Speech recognition · backup engine",
  sarvam: "Speech recognition · India & Others",
  assemblyai: "Speech recognition · US & Canada",
  google: "Summarisation & AI chat",
};

const BUCKET_COLORS = {
  openai: "bg-slate-500",
  sarvam: "bg-amber-600",
  assemblyai: "bg-emerald-700",
  google: "bg-indigo-600",
};

function bucketLabel(provider) {
  return BUCKET_LABELS[provider] || "Other";
}

function bucketColor(provider) {
  return BUCKET_COLORS[provider] || "bg-ink-400";
}

function usd(v) {
  const n = Number(v || 0);
  // Per-CV screening events are sub-cent (~$0.004); 2 dp rendered them all as
  // $0.00 on this admin-only page. Show 4 dp for non-zero sub-cent amounts.
  if (n > 0 && n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

export default function Usage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState("interview"); // "interview" | "interviewer"

  useEffect(() => {
    setLoading(true);
    api
      .get("/usage", { params: { days } })
      .then(({ data }) => setData(data))
      .catch((err) => setData({ error: err?.response?.data?.detail || "Failed to load" }))
      .finally(() => setLoading(false));
  }, [days]);

  // Aggregated cost-by-bucket. The backend returns by_provider with the
  // internal keys; we sum into customer-visible buckets here so two
  // internal vendors that share a bucket (none currently, but defensive)
  // collapse into one row in the UI.
  const buckets = useMemo(() => {
    const acc = {};
    (data?.by_provider || []).forEach((p) => {
      const key = p.provider;
      const label = bucketLabel(key);
      if (!acc[label]) acc[label] = { provider: key, label, est_cost_usd: 0, events: 0 };
      acc[label].est_cost_usd += p.est_cost_usd || 0;
      acc[label].events += p.events || 0;
    });
    return Object.values(acc).sort((a, b) => b.est_cost_usd - a.est_cost_usd);
  }, [data]);

  const visibleBuckets = useMemo(() => buckets.filter((b) => b.est_cost_usd > 0), [buckets]);

  return (
    <AppShell>
      <div data-testid="usage-page" className="space-y-8">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="hp-section-label">Usage & cost</div>
            <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
              Cost instrumentation
            </h1>
            <p className="mt-1 max-w-2xl text-[14px] text-ink-500">
              Internal admin view. Use these numbers to validate per-interview unit economics against your $30/seat/month plan.
            </p>
          </div>
          <div>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="hp-input"
              data-testid="usage-range"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </header>

        {loading && (
          <div className="hp-card flex justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} />
          </div>
        )}

        {!loading && data?.error && (
          <div className="hp-card p-6 text-red-700 text-[14px]" data-testid="usage-error">
            {data.error}
          </div>
        )}

        {!loading && data && !data.error && (
          <>
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Stat
                label={`Total · last ${data.days} days`}
                value={usd(data.total_usd_listprice)}
                testId="usage-total"
              />
              {buckets.map((b) => (
                <Stat
                  key={b.label}
                  label={`${b.label} · ${b.events} call${b.events === 1 ? "" : "s"}`}
                  value={usd(b.est_cost_usd)}
                  testId={`usage-bucket-${b.provider}`}
                />
              ))}
              {buckets.length === 0 && (
                <Stat label="No events yet" value="—" testId="usage-empty" />
              )}
            </section>

            {visibleBuckets.length > 0 && (
              <section className="hp-card p-5" data-testid="usage-bucket-mix">
                <div className="hp-section-label mb-3">Cost mix</div>
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-ink-100">
                  {visibleBuckets.map((b) => {
                    const pct = (b.est_cost_usd / Math.max(data.total_usd_listprice, 0.0001)) * 100;
                    return (
                      <div
                        key={b.label}
                        className={bucketColor(b.provider)}
                        style={{ width: `${pct}%` }}
                        title={`${b.label}: ${usd(b.est_cost_usd)} (${pct.toFixed(1)}%)`}
                        data-testid={`bucket-mix-bar-${b.provider}`}
                      />
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-ink-600">
                  {buckets.map((b) => (
                    <div key={b.label} className="flex items-center gap-1.5">
                      <span className={`inline-block h-2 w-2 rounded-full ${bucketColor(b.provider)}`} />
                      <span>{b.label}</span>
                      <span className="text-ink-400">·</span>
                      <span className="font-mono">{usd(b.est_cost_usd)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="hp-card overflow-hidden">
              <div className="flex items-center justify-between gap-2 border-b border-ink-200 bg-ink-50 px-5 py-3">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
                  <h2 className="font-display text-[15px] font-medium text-ink-900">
                    {groupBy === "interview" ? "Top interviews by cost" : "Cost by interviewer"}
                  </h2>
                </div>
                <div className="flex gap-1 rounded-md border border-ink-200 bg-white p-0.5" data-testid="usage-groupby-toggle">
                  <button
                    onClick={() => setGroupBy("interview")}
                    className={`rounded px-2.5 py-1 text-[12px] font-medium transition ${groupBy === "interview" ? "bg-emerald-800 text-white" : "text-ink-600 hover:bg-ink-50"}`}
                    data-testid="usage-groupby-interview"
                  >
                    By interview
                  </button>
                  <button
                    onClick={() => setGroupBy("interviewer")}
                    className={`rounded px-2.5 py-1 text-[12px] font-medium transition ${groupBy === "interviewer" ? "bg-emerald-800 text-white" : "text-ink-600 hover:bg-ink-50"}`}
                    data-testid="usage-groupby-interviewer"
                  >
                    By interviewer
                  </button>
                </div>
              </div>
              {groupBy === "interview" ? (
                <table className="w-full">
                  <thead className="bg-ink-50/50 border-b border-ink-200">
                    <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                      <th className="px-5 py-3">Interview</th>
                      <th className="px-5 py-3">Interviewer</th>
                      <th className="px-5 py-3">Events</th>
                      <th className="px-5 py-3 text-right">List-price cost</th>
                      <th className="px-5 py-3 w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100" data-testid="usage-top-interviews">
                    {data.top_interviews.length === 0 && (
                      <tr><td colSpan={5} className="px-5 py-8 text-center text-[13px] text-ink-400">No usage yet.</td></tr>
                    )}
                    {data.top_interviews.map((row) => (
                      <tr key={row.interview_id} className="hover:bg-ink-50 transition" data-testid={`usage-row-${row.interview_id}`}>
                        <td className="px-5 py-3 font-mono text-[12px] text-ink-700">{row.interview_id === "__none__" ? "—" : row.interview_id}</td>
                        <td className="px-5 py-3 text-[13px] text-ink-700" data-testid={`usage-row-interviewer-${row.interview_id}`}>
                          {row.interviewer_names?.length ? row.interviewer_names.join(", ") : "—"}
                        </td>
                        <td className="px-5 py-3 text-[13px] text-ink-600">{row.events}</td>
                        <td className="px-5 py-3 text-right font-medium text-ink-900">{usd(row.est_cost_usd)}</td>
                        <td className="px-5 py-3 text-right">
                          {row.interview_id !== "__none__" && (
                            <a href={`/interviews/${row.interview_id}`} className="text-emerald-800 hover:text-emerald-900">
                              <ExternalLink className="h-3.5 w-3.5" strokeWidth={STROKE} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead className="bg-ink-50/50 border-b border-ink-200">
                    <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                      <th className="px-5 py-3">Interviewer</th>
                      <th className="px-5 py-3">Events</th>
                      <th className="px-5 py-3 text-right">List-price cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100" data-testid="usage-by-interviewer">
                    {(data.by_interviewer || []).length === 0 && (
                      <tr><td colSpan={3} className="px-5 py-8 text-center text-[13px] text-ink-400">No usage yet.</td></tr>
                    )}
                    {(data.by_interviewer || []).map((row) => (
                      <tr key={row.recorded_by_user_id || "unattributed"} className="hover:bg-ink-50 transition" data-testid={`usage-interviewer-row-${row.recorded_by_user_id || "unattributed"}`}>
                        <td className="px-5 py-3 text-[13px] text-ink-800">{row.name}</td>
                        <td className="px-5 py-3 text-[13px] text-ink-600">{row.events}</td>
                        <td className="px-5 py-3 text-right font-medium text-ink-900">{usd(row.est_cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="hp-card overflow-hidden">
              <div className="border-b border-ink-200 bg-ink-50 px-5 py-3">
                <h2 className="font-display text-[15px] font-medium text-ink-900">Recent events</h2>
              </div>
              <table className="w-full">
                <thead className="bg-ink-50/50 border-b border-ink-200">
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                    <th className="px-5 py-3">When</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Units</th>
                    <th className="px-5 py-3 text-right">List-price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {data.events.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-[13px] text-ink-400">No events yet.</td></tr>
                  )}
                  {data.events.slice(0, 50).map((e) => (
                    <tr key={e.event_id} className="hover:bg-ink-50 transition" data-testid={`usage-event-${e.event_id}`}>
                      <td className="px-5 py-3 text-[12px] text-ink-500">{new Date(e.timestamp).toLocaleString()}</td>
                      <td className="px-5 py-3 text-[13px] text-ink-800">{bucketLabel(e.provider)}</td>
                      <td className="px-5 py-3 text-[13px] text-ink-700">{e.units.toFixed(2)} {e.units_unit}</td>
                      <td className="px-5 py-3 text-right font-medium text-ink-900">{usd(e.est_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <p className="text-[12px] text-ink-400" data-testid="usage-pricing-basis">
              Cost is list-price-USD reference math computed at event-write time.
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="hp-card p-5" data-testid={testId}>
      <div className="hp-section-label">{label}</div>
      <div className="font-display mt-2 text-3xl font-medium text-ink-900">{value}</div>
    </div>
  );
}
